import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Empty,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type { MarketingCharacterAsset } from './types';
import type {
  MarketingCampaign,
  MarketingCampaignAssetKind,
  MarketingCampaignAssetRef,
  MarketingCampaignChannel,
  MarketingCampaignStatus,
  MarketingSocialAsset,
} from './marketingWorkspaceState';
import { createMarketingId } from './marketingWorkspaceState';
import type { MarketingVideoHistoryItem } from '../MarketingVideoHistoryScreen';

const { Paragraph, Text, Title } = Typography;

interface MarketingCampaignsModuleProps {
  apiBase: string;
  bookId: string;
  userId: string;
  editionLanguage: string;
  campaigns: MarketingCampaign[];
  socialAssets: MarketingSocialAsset[];
  onCampaignsChange: (campaigns: MarketingCampaign[]) => void;
}

interface CampaignAssetOption {
  key: string;
  kind: MarketingCampaignAssetKind;
  id: string;
  label: string;
  detail: string;
}

const refKey = (ref: MarketingCampaignAssetRef): string => `${ref.kind}:${ref.id}`;

const blankCampaign = (): MarketingCampaign => {
  const now = new Date().toISOString();
  return {
    id: createMarketingId('campaign'),
    name: '',
    objective: 'Book launch',
    channel: 'mixed',
    status: 'planning',
    notes: '',
    assetRefs: [],
    createdAt: now,
    updatedAt: now,
  };
};

const MarketingCampaignsModule: React.FC<MarketingCampaignsModuleProps> = ({
  apiBase,
  bookId,
  userId,
  editionLanguage,
  campaigns,
  socialAssets,
  onCampaignsChange,
}) => {
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [videos, setVideos] = useState<MarketingVideoHistoryItem[]>([]);
  const [portraits, setPortraits] = useState<MarketingCharacterAsset[]>([]);
  const [editor, setEditor] = useState<MarketingCampaign | null>(null);

  const loadAssets = useCallback(async () => {
    if (!userId) return;
    setLoadingAssets(true);
    try {
      const historyParams = new URLSearchParams({ userId, page: '1', pageSize: '100', language: editionLanguage });
      const assetParams = new URLSearchParams({ userId });
      const [videoResult, portraitResult] = await Promise.allSettled([
        fetch(`${apiBase}/api/books/${encodeURIComponent(bookId)}/marketing/videos/history?${historyParams.toString()}`),
        fetch(`${apiBase}/api/books/${encodeURIComponent(bookId)}/marketing/characters/assets?${assetParams.toString()}`),
      ]);
      if (videoResult.status === 'fulfilled' && videoResult.value.ok) {
        const payload = await videoResult.value.json();
        setVideos(Array.isArray(payload?.items) ? payload.items : []);
      }
      if (portraitResult.status === 'fulfilled' && portraitResult.value.ok) {
        const payload = await portraitResult.value.json();
        setPortraits(Array.isArray(payload?.items) ? payload.items : []);
      }
    } catch (error) {
      console.error('Failed to load campaign assets', error);
      message.error('Some campaign assets could not be loaded.');
    } finally {
      setLoadingAssets(false);
    }
  }, [apiBase, bookId, editionLanguage, userId]);

  useEffect(() => {
    void loadAssets();
  }, [loadAssets]);

  const assetOptions = useMemo<CampaignAssetOption[]>(() => [
    ...videos.map(video => ({
      key: `video:${video.id}`,
      kind: 'video' as const,
      id: video.id,
      label: video.prompt || video.mode || 'Marketing video',
      detail: `${video.language || editionLanguage} · ${video.durationSeconds || '?'}s`,
    })),
    ...portraits.map(asset => ({
      key: `character:${asset.id}`,
      kind: 'character' as const,
      id: asset.id,
      label: asset.characterName || 'Character portrait',
      detail: asset.stylePreset || asset.visualStyle || 'Portrait',
    })),
    ...socialAssets.map(asset => ({
      key: `social:${asset.id}`,
      kind: 'social' as const,
      id: asset.id,
      label: asset.title,
      detail: `${asset.format.replace('_', ' ')} · ${asset.editionLanguage}`,
    })),
  ], [editionLanguage, portraits, socialAssets, videos]);
  const optionByKey = useMemo(() => new Map(assetOptions.map(option => [option.key, option])), [assetOptions]);

  const saveEditor = () => {
    if (!editor?.name.trim()) {
      message.warning('Give the campaign a name.');
      return;
    }
    const updated = { ...editor, name: editor.name.trim(), updatedAt: new Date().toISOString() };
    onCampaignsChange(campaigns.some(item => item.id === updated.id)
      ? campaigns.map(item => item.id === updated.id ? updated : item)
      : [updated, ...campaigns]);
    setEditor(null);
    message.success('Campaign saved.');
  };

  const deleteCampaign = (campaignId: string) => {
    onCampaignsChange(campaigns.filter(campaign => campaign.id !== campaignId));
  };

  return (
    <div className="marketing-campaigns">
      <section className="marketing-workspace-page-header">
        <div>
          <span className="marketing-workspace__kicker">Plan and organize</span>
          <Title level={3}>Campaigns</Title>
          <Paragraph>Group videos, portraits, and social packs around one audience or promotion.</Paragraph>
        </div>
        <Space wrap>
          <Button icon={<ReloadOutlined />} loading={loadingAssets} onClick={() => void loadAssets()}>Refresh assets</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditor(blankCampaign())}>New campaign</Button>
        </Space>
      </section>

      <Spin spinning={loadingAssets}>
        {campaigns.length ? (
          <div className="marketing-campaign-list">
            {campaigns.map(campaign => {
              const availableCount = campaign.assetRefs.filter(ref => optionByKey.has(refKey(ref))).length;
              const missingCount = campaign.assetRefs.length - availableCount;
              return (
                <article key={campaign.id} className="marketing-campaign-card">
                  <div className="marketing-campaign-card__header">
                    <div>
                      <Space wrap size={6}>
                        <Title level={4}>{campaign.name}</Title>
                        <Tag color={campaign.status === 'completed' ? 'green' : campaign.status === 'active' ? 'blue' : 'default'}>
                          {campaign.status}
                        </Tag>
                        <Tag>{campaign.channel}</Tag>
                      </Space>
                      <Text type="secondary">{campaign.objective}</Text>
                    </div>
                    <Space>
                      <Button icon={<EditOutlined />} onClick={() => setEditor({ ...campaign, assetRefs: [...campaign.assetRefs] })}>Edit</Button>
                      <Popconfirm title="Delete this campaign plan?" onConfirm={() => deleteCampaign(campaign.id)}>
                        <Button danger icon={<DeleteOutlined />} aria-label={`Delete ${campaign.name}`} />
                      </Popconfirm>
                    </Space>
                  </div>
                  {campaign.notes ? <Paragraph>{campaign.notes}</Paragraph> : null}
                  <div className="marketing-campaign-card__assets">
                    <Text strong>{campaign.assetRefs.length} asset{campaign.assetRefs.length === 1 ? '' : 's'}</Text>
                    <Space wrap size={6}>
                      {campaign.assetRefs.map(ref => {
                        const option = optionByKey.get(refKey(ref));
                        return option
                          ? <Tag key={refKey(ref)}>{option.kind}: {option.label}</Tag>
                          : <Tag color="red" key={refKey(ref)}>Missing {ref.kind} asset</Tag>;
                      })}
                      {missingCount ? <Text type="secondary">{missingCount} reference{missingCount === 1 ? '' : 's'} no longer available.</Text> : null}
                    </Space>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Create a campaign to group the assets for a launch, character reveal, trope, sale, or evergreen promotion."
          >
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditor(blankCampaign())}>Create first campaign</Button>
          </Empty>
        )}
      </Spin>

      <Modal
        title={editor && campaigns.some(item => item.id === editor.id) ? 'Edit campaign' : 'New campaign'}
        open={Boolean(editor)}
        onCancel={() => setEditor(null)}
        onOk={saveEditor}
        okText="Save campaign"
        width={760}
      >
        {editor ? (
          <div className="marketing-campaign-editor">
            <label><span>Name</span><Input value={editor.name} onChange={event => setEditor({ ...editor, name: event.target.value })} placeholder="Launch week" /></label>
            <label><span>Objective</span><Input value={editor.objective} onChange={event => setEditor({ ...editor, objective: event.target.value })} placeholder="Introduce the central relationship" /></label>
            <label>
              <span>Channel</span>
              <Select<MarketingCampaignChannel>
                value={editor.channel}
                onChange={channel => setEditor({ ...editor, channel })}
                options={[
                  { value: 'mixed', label: 'Mixed channels' },
                  { value: 'tiktok', label: 'TikTok' },
                  { value: 'instagram', label: 'Instagram' },
                  { value: 'facebook', label: 'Facebook' },
                  { value: 'youtube', label: 'YouTube' },
                ]}
              />
            </label>
            <label>
              <span>Status</span>
              <Select<MarketingCampaignStatus>
                value={editor.status}
                onChange={status => setEditor({ ...editor, status })}
                options={[
                  { value: 'planning', label: 'Planning' },
                  { value: 'active', label: 'Active' },
                  { value: 'completed', label: 'Completed' },
                ]}
              />
            </label>
            <label className="marketing-campaign-editor__full"><span>Notes</span><Input.TextArea rows={3} value={editor.notes} onChange={event => setEditor({ ...editor, notes: event.target.value })} /></label>
            <div className="marketing-campaign-editor__full">
              <Text strong>Assets</Text>
              <Paragraph type="secondary">Mix completed videos, character portraits, and saved social packs.</Paragraph>
              {assetOptions.length ? (
                <Checkbox.Group
                  className="marketing-campaign-asset-picker"
                  value={editor.assetRefs.map(refKey)}
                  onChange={keys => setEditor({
                    ...editor,
                    assetRefs: keys.map(key => optionByKey.get(String(key)))
                      .filter((option): option is CampaignAssetOption => Boolean(option))
                      .map(option => ({ kind: option.kind, id: option.id })),
                  })}
                >
                  {assetOptions.map(option => (
                    <Checkbox key={option.key} value={option.key}>
                      <span><strong>{option.label}</strong><small>{option.kind} · {option.detail}</small></span>
                    </Checkbox>
                  ))}
                </Checkbox.Group>
              ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Create or save an asset first." />}
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default MarketingCampaignsModule;

